import type { ClassValue } from 'clsx';

import { getStockStatusChecks, type ProductLabel, type StockInfo } from '../../../../product/index';
import { clsxm } from '@vs-shop/utils';

import {
  ProductCardStock,
  ProductCardStockNote,
  ProductCardStockStatus,
  type ProductCardVariant,
} from '@vs-shop/commerce-ui/productCard';
import { ProductStockVariant } from '@vs-shop/commerce-ui/productStock';

import { promoFreeShipping } from '../../../../config/order/promo';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../utils/intl';

import ProductInStock from '../../page/product/panel/ProductInStock';
import ProductStockTooltip from './ProductStockTooltip';

type Props = {
  stock: StockInfo;
  labels: readonly ProductLabel[];
  variant: ProductCardVariant;
  className?: ClassValue;
};

/**
 * rcprofi's availability copy in the card's stock column.
 *
 * The column's alignment per appearance, and whether the note above it survives
 * a compact card, are the section's. The stock vocabulary — free shipping,
 * "sold out", the quantity tooltip — is this tenant's.
 */
export default function ProductCardStockAndFreeShipping({
  stock,
  labels,
  variant,
  className,
}: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  const { isSoldOut } = getStockStatusChecks(stock.status);

  const hasFreeShipping = labels.find((label) => label.code === promoFreeShipping);

  return (
    <ProductCardStock variant={variant} className={clsxm(className)}>
      {/* FREE SHIPPING */}
      {hasFreeShipping && (
        <ProductCardStockNote variant={variant}>
          {intl.formatMessage({
            id: 'productCard.stockAndFreeShipping.freeShipping',
            defaultMessage: 'Doprava zdarma',
          })}
        </ProductCardStockNote>
      )}

      {/* STOCK */}
      <ProductCardStockStatus>
        {isSoldOut ? (
          <span className="text-foreground-muted">
            {intl.formatMessage({
              id: 'productCard.stockAndFreeShipping.notInStock',
              defaultMessage: 'Vyprodáno',
            })}
          </span>
        ) : (
          <ProductStockTooltip stock={stock}>
            <ProductInStock stock={stock} variant={ProductStockVariant.PLAIN} />
          </ProductStockTooltip>
        )}
      </ProductCardStockStatus>
    </ProductCardStock>
  );
}
